// admin/src/screens/UserDetail.jsx — the account view + THE money-moving form (comp/deduct).
//
// Money discipline surfaced honestly (D1-DESIGN §6.3):
//  • ONE idempotency ref per submission INTENT — minted lazily, reused on every retry of the
//    same intent (a would-go-negative refusal writes NOTHING, so the confirm resend reuses it
//    safely), cleared on success and whenever the amount/action changes (a new intent).
//  • comp = grant (+), deduct = negative adjust (−). There is no balance-set field, by design.
//  • deduct-below-zero needs the deliberate `allow_negative` flag AND an explicit confirm; the
//    server's refusal shape is surfaced verbatim if it ever comes back (a race changed balance).
//  • a duplicate (same ref) is reported as absorbed — nothing moved twice.
//  • the reason note is required — it lands on the ledger row and the audit trail.

import React, { useEffect, useState, useRef } from 'react';
import { navigate } from '@common/router.js';
import { useAdmin } from '../App.jsx';
import { newIdemKey } from '@common/commands.js';
import { Banner, Field, RolePill, fmtInt, fmtDate, fmtDateTime, shortId } from '../lib/ui.jsx';

const LEDGER_PAGE = 50;

export default function UserDetail({ id }) {
  const { api } = useAdmin();
  const [d, setD] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerMore, setLedgerMore] = useState(false);
  const [form, setForm] = useState({ action: 'comp', credits: '', reason: '', expires_at: '', allow_negative: false });
  const [result, setResult] = useState(null); // { kind:'ok'|'err', text }
  const [confirm, setConfirm] = useState(null); // { balance, requested } — the negative gate
  const [busy, setBusy] = useState(false);
  const refToken = useRef(null);

  async function load() {
    const r = await api.get(`admin/users/${id}`);
    if (r.kind === 'ok') {
      setD(r.body);
      setLedger(r.body.ledger || []);
      setLedgerMore((r.body.ledger || []).length >= LEDGER_PAGE);
      setLoadErr(null);
    } else setLoadErr(r.error);
  }
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMore() {
    const last = ledger[ledger.length - 1];
    if (!last) return;
    const r = await api.get(`admin/users/${id}?before_id=${last.id}&limit=${LEDGER_PAGE}`);
    if (r.kind === 'ok') {
      const more = r.body.ledger || [];
      setLedger((cur) => [...cur, ...more]);
      setLedgerMore(more.length >= LEDGER_PAGE);
    }
  }

  // a new intent (action or amount changed) spends the current ref and drops any pending confirm
  function setField(patch) {
    if (('action' in patch) || ('credits' in patch)) { refToken.current = null; setConfirm(null); }
    setForm((f) => ({ ...f, ...patch }));
  }

  async function submit(force = false) {
    setResult(null);
    const credits = Number(form.credits);
    if (!Number.isSafeInteger(credits) || credits <= 0) { setResult({ kind: 'err', text: 'Credits must be a positive whole number.' }); return; }
    if (!form.reason.trim()) { setResult({ kind: 'err', text: 'A reason note is required — it lands on the ledger and the audit trail.' }); return; }
    if (form.expires_at && form.action !== 'comp') { setResult({ kind: 'err', text: 'An expiry only applies to a comp (a deduction never expires).' }); return; }

    const allowNeg = form.action === 'deduct' && (force || form.allow_negative);
    // client-side gate: a deduct that would go negative needs the explicit confirm first
    if (form.action === 'deduct' && !force && (Number(d.balance) - credits < 0)) {
      setConfirm({ balance: Number(d.balance), requested: credits });
      return;
    }

    if (!refToken.current) refToken.current = newIdemKey(); // ONE token per submission intent
    setBusy(true);
    const r = await api.post(`admin/users/${id}/credits`, {
      action: form.action, credits, reason: form.reason.trim(), ref: refToken.current,
      ...(form.action === 'comp' && form.expires_at ? { expires_at: new Date(form.expires_at).toISOString() } : {}),
      ...(allowNeg ? { allow_negative: true } : {}),
    });
    setBusy(false);
    setConfirm(null);

    if (r.kind === 'refused') {
      // the server's would-go-negative refusal — surfaced verbatim; keep the ref for the confirm resend
      setConfirm({ balance: Number(r.body.balance), requested: Number(r.body.requested) });
      setResult({ kind: 'err', text: r.error });
    } else if (r.kind === 'invalid' || r.kind === 'auth') {
      setResult({ kind: 'err', text: r.error });
    } else if (r.kind === 'ok') {
      refToken.current = null; // this intent is spent
      if (r.body.duplicate) setResult({ kind: 'ok', text: 'Already applied (same ref) — nothing moved twice.' });
      else setResult({ kind: 'ok', text: `Done — ${form.action === 'comp' ? 'comped' : 'deducted'} ${fmtInt(credits)}; balance now ${fmtInt(r.body.balance_after)}.` });
      setForm((f) => ({ ...f, credits: '', reason: '', expires_at: '' }));
      load();
    } else {
      setResult({ kind: 'err', text: r.error || 'Something went wrong.' });
    }
  }

  if (loadErr && !d) return <div><Link to="/">‹ Users</Link><Banner kind="err">{loadErr}</Banner></div>;
  if (!d) return <p className="muted">Loading…</p>;

  const isDeduct = form.action === 'deduct';
  const projected = form.credits && Number.isFinite(Number(form.credits))
    ? Number(d.balance) + (isDeduct ? -1 : 1) * Number(form.credits) : null;

  return (
    <div>
      <div className="pagehead">
        <h1><a className="crumb" href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Users</a> / {d.user.email} <RolePill role={d.user.role} /></h1>
        <span className="muted">{d.user.status} · joined {fmtDate(d.user.created_at)}</span>
      </div>

      {/* balance + health */}
      <div className="card">
        <div className="row" style={{ gap: 26 }}>
          <span className="stat"><b className={Number(d.balance) < 0 ? 'neg' : ''}>{fmtInt(d.balance)}</b><span>balance</span></span>
          <span className="stat"><b className="muted">{fmtInt(d.derived_balance)}</b><span>derived (Σ ledger)</span></span>
          {d.drift !== 0 && <span className="pill warn">DRIFT {d.drift}</span>}
          {d.subscription && <span className="stat"><b style={{ fontSize: 14 }}>{d.subscription.label}</b><span>{d.subscription.status} plan</span></span>}
        </div>
        {d.holds.length > 0 && (
          <div style={{ marginBlockStart: 10 }}>
            <div className="hint">live holds — credits reserved by in-flight jobs (released on settle)</div>
            <div className="chips" style={{ marginBlockStart: 4 }}>
              {d.holds.map((h) => <span key={h.job_id} className="chip">{fmtInt(h.held)} · job {shortId(h.job_id)} · {h.job_state}</span>)}
            </div>
          </div>
        )}
      </div>

      {/* the money mover */}
      <div className="card">
        <h2>Move credits</h2>
        <div className="grid" style={{ maxInlineSize: 720 }}>
          <Field label="Action">
            <select value={form.action} onChange={(e) => setField({ action: e.target.value, allow_negative: false })}>
              <option value="comp">Comp — grant (+)</option>
              <option value="deduct">Deduct — adjust (−)</option>
            </select>
          </Field>
          <Field label="Credits">
            <input type="number" min="1" step="1" placeholder="e.g. 500" value={form.credits}
              onChange={(e) => setField({ credits: e.target.value })} />
          </Field>
          {!isDeduct && (
            <Field label="Expires (optional)" hint="a promo comp that lapses; blank = permanent">
              <input type="date" value={form.expires_at} onChange={(e) => setField({ expires_at: e.target.value })} />
            </Field>
          )}
          <Field label="Reason (required — audited)" style={{ gridColumn: '1 / -1' }}>
            <input placeholder="why this movement — lands on the ledger + audit trail" value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
          </Field>
        </div>
        <div className="row">
          {isDeduct && (
            <label className="check"><input type="checkbox" checked={form.allow_negative}
              onChange={(e) => setForm((f) => ({ ...f, allow_negative: e.target.checked }))} /> allow negative balance</label>
          )}
          {projected != null && <span className="hint">balance after: <b className={projected < 0 ? 'neg mono' : 'mono'}>{fmtInt(projected)}</b></span>}
          <span className="spacer" />
          <button className={'btn ' + (isDeduct ? 'danger' : 'primary')} disabled={busy} onClick={() => submit(false)}>
            {isDeduct ? 'Deduct' : 'Comp'}
          </button>
        </div>

        {confirm && (
          <div className="confirm">
            <b>This deduction takes the balance negative.</b>
            <p className="muted" style={{ margin: '4px 0 8px' }}>
              {fmtInt(confirm.balance)} − {fmtInt(confirm.requested)} = <b className="neg">{fmtInt(confirm.balance - confirm.requested)}</b>.
              The account then refuses new jobs until it's topped up. This is a deliberate override.
            </p>
            <div className="row">
              <button className="btn danger" disabled={busy} onClick={() => submit(true)}>Deduct into negative</button>
              <button className="btn ghost" onClick={() => setConfirm(null)}>Cancel</button>
            </div>
          </div>
        )}
        {result && <Banner kind={result.kind === 'ok' ? 'ok' : 'err'}>{result.text}</Banner>}
      </div>

      {/* ledger — keyset paged, newest first */}
      <h2>Ledger <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>(newest first)</span></h2>
      <table>
        <thead><tr><th>id</th><th>kind</th><th className="num">Δ</th><th className="num">after</th><th>note</th><th>ref</th><th>expires</th><th>at</th></tr></thead>
        <tbody>
          {ledger.map((l) => (
            <tr key={l.id}>
              <td className="mono">{l.id}</td><td>{l.kind}</td>
              <td className={'mono num' + (l.delta_credits < 0 ? ' neg' : '')}>{fmtInt(l.delta_credits)}</td>
              <td className="mono num">{fmtInt(l.balance_after)}</td>
              <td className="muted">{l.note || '—'}</td>
              <td className="mono muted" style={{ maxInlineSize: 200, overflowWrap: 'anywhere' }}>{l.external_ref || '—'}</td>
              <td className="muted">{l.expires_at ? fmtDate(l.expires_at) : '—'}</td>
              <td className="muted">{fmtDateTime(l.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {ledgerMore && <div className="row"><button className="btn tiny" onClick={loadMore}>Load more</button></div>}
      {ledger.length === 0 && <p className="muted">No ledger rows.</p>}

      {/* jobs slice */}
      <h2>Recent jobs</h2>
      {d.jobs.length === 0 ? <p className="muted">No jobs.</p> : (
        <table>
          <thead><tr><th>job</th><th>kind</th><th>state</th><th>run</th><th>basis</th><th>created</th></tr></thead>
          <tbody>
            {d.jobs.map((j) => (
              <tr key={j.job_id}>
                <td className="mono">{shortId(j.job_id)}</td><td>{j.kind}</td>
                <td>{j.state}{j.error ? <span className="err"> · {j.error}</span> : ''}</td>
                <td className="mono muted">{j.run_id || '—'}</td><td>{j.confirm_basis || '—'}</td>
                <td className="muted">{fmtDateTime(j.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// a tiny Link shim for the load-error early return (avoids importing the router Link at top
// just for one edge; navigate is already imported)
function Link({ to, children }) {
  return <a className="crumb" href={to} onClick={(e) => { e.preventDefault(); navigate(to); }}>{children}</a>;
}
